import {Injectable} from '@angular/core';
import {BackgroundRunner} from "@capacitor/background-runner";
import {StorageService} from "./storage.service";
import {BackgroundUploadTaskInterface} from "../interfaces/background-upload-task.interface";
import {PreparedUploadInterface} from "../interfaces/prepared-upload.interface";
import {UploadPlatformStrategyService} from "./upload-platform-strategy.service";

@Injectable({
  providedIn: 'root'
})
export class BackgroundUploadOrchestratorService {
  private static readonly STORAGE_KEY = 'wtg.upload.queue.local.v1';
  private static readonly RUNNER_LABEL = 'com.cg.wtg.upload.background.task';

  constructor(
    private readonly storage: StorageService,
    private readonly uploadPlatformStrategy: UploadPlatformStrategyService,
  ) {
  }

  public async init(): Promise<void> {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return;
    }
    await this.dispatchRunnerEvent('processUploadQueue', {});
  }

  public async enqueue(preparedUpload: PreparedUploadInterface, endpoint: string): Promise<void> {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return;
    }
    const task: BackgroundUploadTaskInterface = {
      id: preparedUpload.clientUploadId,
      flow: preparedUpload.flow,
      endpoint,
      mimeType: preparedUpload.file?.type || '',
      fileName: preparedUpload.file?.name || '',
      sizeBytes: preparedUpload.sizeAfterBytes || preparedUpload.file?.size || 0,
      status: 'queued',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    this.upsertLocalTask(task);
    await this.dispatchRunnerEvent('enqueueUploadTask', {task});
    await this.dispatchRunnerEvent('processUploadQueue', {});
  }

  public async markUploading(taskId: string): Promise<void> {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return;
    }
    this.patchLocalTask(taskId, {status: 'uploading', updatedAt: Date.now()});
    await this.dispatchRunnerEvent('updateUploadTask', {task: this.findLocalTask(taskId)});
  }

  public async markUploaded(taskId: string, serverResourceId?: string): Promise<void> {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return;
    }
    this.patchLocalTask(taskId, {
      status: 'uploaded',
      serverResourceId,
      updatedAt: Date.now(),
      error: undefined,
    });
    await this.dispatchRunnerEvent('removeUploadTask', {taskId});
    this.removeLocalTask(taskId);
  }

  public async markFailed(taskId: string, errorMessage?: string): Promise<void> {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return;
    }
    this.patchLocalTask(taskId, {
      status: 'failed',
      error: errorMessage || 'Upload failed',
      updatedAt: Date.now(),
    });
    await this.dispatchRunnerEvent('updateUploadTask', {task: this.findLocalTask(taskId)});
  }

  public getLocalPendingTasks(): BackgroundUploadTaskInterface[] {
    if (!this.uploadPlatformStrategy.isNativeBackgroundMode()) {
      return [];
    }
    return this.readLocalTasks().filter((task) => task.status === 'queued' || task.status === 'uploading' || task.status === 'failed');
  }

  private readLocalTasks(): BackgroundUploadTaskInterface[] {
    const raw = this.storage.getItem(BackgroundUploadOrchestratorService.STORAGE_KEY);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  private saveLocalTasks(tasks: BackgroundUploadTaskInterface[]): void {
    this.storage.setItem(BackgroundUploadOrchestratorService.STORAGE_KEY, JSON.stringify(tasks));
  }

  private upsertLocalTask(task: BackgroundUploadTaskInterface): void {
    const tasks = this.readLocalTasks();
    const idx = tasks.findIndex((item) => item.id === task.id);
    if (idx >= 0) {
      tasks[idx] = {...tasks[idx], ...task};
    } else {
      tasks.push(task);
    }
    this.saveLocalTasks(tasks);
  }

  private patchLocalTask(taskId: string, patch: Partial<BackgroundUploadTaskInterface>): void {
    const tasks = this.readLocalTasks();
    const idx = tasks.findIndex((item) => item.id === taskId);
    if (idx < 0) {
      return;
    }
    tasks[idx] = {...tasks[idx], ...patch};
    this.saveLocalTasks(tasks);
  }

  private removeLocalTask(taskId: string): void {
    const tasks = this.readLocalTasks().filter((item) => item.id !== taskId);
    this.saveLocalTasks(tasks);
  }

  private findLocalTask(taskId: string): BackgroundUploadTaskInterface | null {
    return this.readLocalTasks().find((item) => item.id === taskId) || null;
  }

  private async dispatchRunnerEvent(event: string, details: Record<string, unknown>): Promise<void> {
    if (!this.isNativeRunnerAvailable()) {
      return;
    }
    try {
      await BackgroundRunner.dispatchEvent({
        label: BackgroundUploadOrchestratorService.RUNNER_LABEL,
        event,
        details,
      });
    } catch (_) {
      // Graceful fallback to local queue only.
    }
  }

  private isNativeRunnerAvailable(): boolean {
    return this.uploadPlatformStrategy.isNativeBackgroundMode();
  }
}
